import { useState } from 'react'
import type { ContactFormData } from '@/types'

interface UseContactFormResult {
  formData: ContactFormData
  isSubmitting: boolean
  isSubmitted: boolean
  error: string | null
  handleChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void
  handleSubmit: (e: React.FormEvent) => Promise<void>
  reset: () => void
}

const initialFormData: ContactFormData = {
  name: '',
  email: '',
  company: '',
  phone: '',
  type: 'demo',
  message: '',
  preferredDate: '',
  preferredTime: '',
  website: '', // honeypot
}

export function useContactForm(inquiryType: ContactFormData['type'] = 'demo'): UseContactFormResult {
  const [formData, setFormData] = useState<ContactFormData>({
    ...initialFormData,
    type: inquiryType,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)

    // Honeypot check - if website field is filled, it's a bot
    if (formData.website) {
      setIsSubmitting(false)
      setIsSubmitted(true)
      return
    }

    // Extract UTM parameters from URL if present
    const urlParams = new URLSearchParams(window.location.search)
    const utmSource = urlParams.get('utm_source') || undefined
    const utmMedium = urlParams.get('utm_medium') || undefined
    const utmCampaign = urlParams.get('utm_campaign') || undefined

    try {
      // Send to WealthSpaceAPI public enquiries endpoint
      const apiUrl = import.meta.env.VITE_WEALTHSPACE_API_URL || 'https://api.wealthspace.co.uk'
      const apiKey = import.meta.env.VITE_BROCHURE_API_KEY || ''

      const response = await fetch(`${apiUrl}/api/v1/public/brochure-enquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey,
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company || undefined,
          phone: formData.phone || undefined,
          type: formData.type,
          message: formData.message,
          preferredDate: formData.preferredDate || undefined,
          preferredTime: formData.preferredTime || undefined,
          sourcePage: window.location.pathname,
          utmSource,
          utmMedium,
          utmCampaign,
        }),
      })

      if (response.ok) {
        setIsSubmitted(true)
      } else {
        const data = await response.json().catch(() => ({}))
        setError(data.detail || data.message || 'There was a problem sending your message. Please try again.')
      }
    } catch {
      setError('There was a problem sending your message. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const reset = () => {
    setFormData({ ...initialFormData, type: inquiryType })
    setIsSubmitted(false)
    setError(null)
  }

  return {
    formData,
    isSubmitting,
    isSubmitted,
    error,
    handleChange,
    handleSubmit,
    reset,
  }
}
